'use client';

import { useRef } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { motion } from 'framer-motion';
import { ArrowRight, CalendarDays, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HeaderLanding from '@/components/headerlanding';
import BerryCluster from '@/components/brand/BerryCluster';
import { KineticLine } from '@/components/hero/KineticHeadline';
import ChapterRail from '@/components/landing/ChapterRail';
import HeroProductPanel from '@/components/hero/HeroProductPanel';
import HeroAtmosphere from '@/components/hero/HeroAtmosphere';
import ScrambleText from '@/components/hero/ScrambleText';
import MagneticButton from '@/components/hero/MagneticButton';
import WordCycle from '@/components/hero/WordCycle';
import { bookDemoHref } from '@/lib/links';

const WhyBlueberries = dynamic(() => import('@/components/landing/WhyBlueberries'));
const PlatformSection = dynamic(() => import('@/components/landing/PlatformSection'));
const CategorySection = dynamic(() => import('@/components/landing/CategorySection'));
const ContactForm = dynamic(() => import('@/components/ContactForm'));
const SecurityBadges = dynamic(() => import('@/components/SecurityBadges'));
const LogoMarquee = dynamic(() => import('@/components/LogoMarquee'));

gsap.registerPlugin(ScrollTrigger);
if (typeof window !== 'undefined') {
  gsap.registerPlugin(useGSAP);
}

const EASE = [0.16, 1, 0.3, 1] as const;

export default function Home() {
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  useGSAP(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    // Top scroll-progress bar
    if (progressRef.current) {
      gsap.fromTo(progressRef.current, { scaleX: 0 }, {
        scaleX: 1, ease: 'none', transformOrigin: 'left',
        scrollTrigger: { trigger: document.documentElement, start: 'top top', end: 'bottom bottom', scrub: 0.1 },
      });
    }

    const mm = gsap.matchMedia();

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    const t = setTimeout(refresh, 800);
    return () => { window.removeEventListener('load', refresh); clearTimeout(t); mm.revert(); };
  }, { scope: containerRef });

  return (
    <div ref={containerRef} style={{ backgroundColor: '#FBFAFF' }}>
      {/* Scroll progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-transparent pointer-events-none">
        <div ref={progressRef} className="h-full w-full origin-left scale-x-0 bg-gradient-to-r from-[#A78BFA] via-[#8B5CF6] to-[#6D28D9]" />
      </div>

      <style>{`
        @keyframes shimmer { 0%{ background-position: 0% 50% } 100%{ background-position: 200% 50% } }
        @keyframes shimmer2 { 0%{ background-position: 0% 50% } 100%{ background-position: 200% 50% } }
        @keyframes heroGrad { 0%{ background-position: 0% 50% } 100%{ background-position: 200% 50% } }
        @keyframes hpSheen { 0% { transform: translateX(-20%) rotate(12deg); opacity: 0 } 10% { opacity: 0.7 } 35% { opacity: 0 } 100% { transform: translateX(560%) rotate(12deg); opacity: 0 } }
        @keyframes beamPulse { 0%,100%{ opacity: 0.45; transform: translateX(-50%) scaleY(1) } 50%{ opacity: 0.7; transform: translateX(-50%) scaleY(1.06) } }
        @keyframes floatSlow { 0%,100%{ transform: translate(0,0) } 50%{ transform: translate(8px,-10px) } }
        @keyframes horizonSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes ruleDraw { from { transform: scaleX(0) } to { transform: scaleX(1) } }
        @keyframes dustDrift { 0%,100% { transform: translate3d(0,0,0) } 50% { transform: translate3d(0,-12px,0) } }
        /* Subtle scroll-padding so anchored elements aren't hidden under the sticky header */
        html { scroll-behavior: smooth; scroll-padding-top: 72px; }
        /* Smoother selection */
        ::selection { background: rgba(124,58,237,0.22); color: #1a1027; }
      `}</style>

      <HeaderLanding />

      <ChapterRail
        items={[
          { id: 'hero', label: 'Intro' },
          { id: 'why', label: 'Why' },
          { id: 'spine', label: 'Platform' },
          { id: 'impact', label: 'Impact' },
          { id: 'cta', label: 'Get started' },
        ]}
      />

      {/* ══════════════════════ HERO — focused brand moment ══════════════════════ */}
      <section id="hero" ref={heroRef} className="relative">
        <div
          className="relative min-h-screen w-full overflow-x-clip text-gray-900"
          style={{ backgroundColor: '#FBFAFF' }}
        >
          {/* Background clip wrapper — contains the auroras so they never overflow the page */}
          <div aria-hidden className="absolute inset-0 overflow-hidden">
          {/* Single hairline grid, gently masked toward the headline */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(76,29,149,1) 1px, transparent 1px), linear-gradient(90deg, rgba(76,29,149,1) 1px, transparent 1px)',
              backgroundSize: '72px 72px',
              maskImage: 'radial-gradient(ellipse 75% 65% at 50% 32%, #000 0%, transparent 78%)',
              WebkitMaskImage: 'radial-gradient(ellipse 75% 65% at 50% 32%, #000 0%, transparent 78%)',
            }}
          />

          {/* Readability veil so type stays crisp over the glow */}
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(72% 80% at 30% 42%, rgba(252,251,255,0.82) 0%, rgba(252,251,255,0.30) 46%, transparent 72%)' }} />

          {/* C — Film-grain noise texture: zero performance cost, big polish lift */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '180px 180px',
              opacity: 0.042,
              mixBlendMode: 'overlay',
            }}
          />
          </div>


          {/* Content — split: copy left, live product panel right */}
          <div className="relative z-10 grid min-h-screen w-full grid-cols-1 items-stretch gap-x-6 gap-y-12 px-6 pt-20 pb-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-x-6 lg:px-12 xl:px-16 lg:pt-16">
            <div className="relative z-20 flex w-full flex-col items-start justify-center text-left">
              {/* ── TOP GROUP — pill + headline + paragraph (pins to top) ── */}
              <div className="w-full">
              {/* Status pill */}
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, duration: 0.55, ease: EASE }}
                className="inline-flex items-center gap-2.5 rounded-full border border-purple-200/40 bg-white/40 backdrop-blur-sm px-3.5 py-1.5 shadow-[0_1px_0_rgba(255,255,255,0.5)_inset]"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-purple-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-600" />
                </span>
                <span className="text-[11px] tracking-[0.22em] uppercase font-medium text-purple-800">
                  The AI-native FP&amp;A platform
                </span>
              </motion.div>

              {/* Monumental kinetic headline — big, 3-line wrap like the target */}
              <h1 className="mt-6 text-left font-semibold tracking-[-0.06em] leading-[0.98] text-[clamp(2.25rem,4.8vw,5.25rem)]">
                <KineticLine words={['Numbers', 'you', 'can']} delay={0.12} light justify="start" />
                <br />
                {/* E — Word cycle: flips count on → rely on → share → trust. */}
                <WordCycle delay={0.46} />
              </h1>

              {/* Company description — the specific value prop, in one sentence */}
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
                className="mt-8 max-w-xl text-left text-[16px] md:text-[17.5px] text-gray-800 leading-[1.6] font-normal"
              >
                We help companies get <span className="font-semibold text-gray-900">real-time profitability by employee, product and client</span> by combining <span className="font-medium text-gray-900">financial, operational, and communication data</span> into one unified view.
              </motion.p>

              {/* What you get — 3-point scannable list that translates the paragraph into concrete deliverables */}
              <motion.ul
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { delayChildren: 0.7, staggerChildren: 0.07 } },
                }}
                className="mt-7 max-w-xl space-y-2.5"
              >
                {[
                  'Profitability by employee, product, and client',
                  'Updates live, in real time. No manual work.',
                  'Connects to every system you already use',
                ].map((line) => (
                  <motion.li
                    key={line}
                    variants={{
                      hidden: { opacity: 0, x: -8 },
                      show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE } },
                    }}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 ring-1 ring-purple-200/60">
                      <Check className="h-3 w-3 text-purple-700" strokeWidth={3} />
                    </span>
                    <span className="text-[14.5px] md:text-[15.5px] text-gray-700 leading-[1.45]">{line}</span>
                  </motion.li>
                ))}
              </motion.ul>
              </div>

              {/* ── CTAs — flow right after the paragraph ── */}
              <div className="w-full mt-8">
              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.82, duration: 0.55, ease: EASE }}
                className="flex flex-col sm:flex-row gap-5 items-start sm:items-center"
              >
                {/* B — Magnetic pull on both CTAs */}
                <MagneticButton>
                  <Link href="/products" className="group inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-7 py-3.5 text-sm md:text-base font-medium transition-all hover:bg-black hover:-translate-y-0.5 shadow-[0_14px_34px_-12px_rgba(17,17,17,0.5)]">
                    See how it works
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </MagneticButton>

                <MagneticButton>
                  <Link
                    href={bookDemoHref('home_hero')}
                    className="group inline-flex items-center gap-3 text-gray-700 hover:text-gray-900 text-sm md:text-base font-medium transition-colors"
                  >
                    <span className="relative flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 group-hover:border-gray-500 group-hover:bg-gray-50 transition-all">
                      <CalendarDays className="h-4 w-4 text-gray-900" />
                    </span>
                    Book a demo
                  </Link>
                </MagneticButton>
              </motion.div>

              {/* Trusted-by line — small social-proof note below the CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05, duration: 0.6, ease: EASE }}
                className="mt-7 flex items-center gap-3 text-[12px] text-gray-500"
              >
                {/* Tiny avatar stack */}
                <div className="flex -space-x-1.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-purple-200 to-purple-300 ring-2 ring-white text-[9px] font-bold text-purple-800">AC</span>
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-violet-200 to-violet-300 ring-2 ring-white text-[9px] font-bold text-violet-800">NW</span>
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 ring-2 ring-white text-[9px] font-bold text-indigo-800">GX</span>
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-200 to-fuchsia-300 ring-2 ring-white text-[9px] font-bold text-fuchsia-800">IT</span>
                </div>
                <span className="flex items-center gap-2 font-medium text-gray-600">
                  <span>Trusted by</span>
                  <span className="text-gray-900 font-semibold">50+ finance teams</span>
                </span>
              </motion.div>

              </div>

            </div>

            {/* RIGHT — live product panel */}
            <div className="relative z-10 flex w-full items-stretch justify-center lg:justify-end">
              <HeroProductPanel />
            </div>
          </div>

        </div>
      </section>


      {/* ══════════════════════ WHY BLUEBERRIES — before/after + outcomes ══════════════════════ */}
      <WhyBlueberries />

      {/* ══════════════════════ THE PLATFORM — data flow + capabilities ══════════════════════ */}
      <PlatformSection />

      {/* ══════════════════════ IMPACT — category + payoffs ══════════════════════ */}
      <CategorySection />

      {/* ══════════════════════ CTA bookend — purple finale (matches heroes) ══════════════════════ */}
      <section id="cta" className="relative py-20 md:py-28 overflow-hidden bg-[#6D28D9] text-white">
        {/* Soft blobs — same palette as the hero sections */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-purple-400/25 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-violet-300/20 blur-3xl" />
          <div className="absolute left-1/2 top-1/2 h-[42vw] w-[42vw] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-50 blur-[130px]" style={{ background: 'radial-gradient(circle, rgba(192,132,252,0.5), transparent 65%)' }} />
        </div>

        <div className="container px-6 mx-auto max-w-4xl text-center space-y-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ delay: 0.2, duration: 0.8 }} className="space-y-5">
            <div className="text-[11px] tracking-[0.32em] uppercase text-purple-200 font-medium">Get started</div>
            <h2 className="text-[clamp(2.5rem,7vw,5.5rem)] font-semibold leading-[0.95] tracking-[-0.035em]">
              Ready to automate<br />
              <span className="text-white">your FP&amp;A?</span>
            </h2>
            <p className="text-lg md:text-xl text-purple-200 max-w-xl mx-auto font-light leading-relaxed">
              Join finance teams who trust Blueberries. for accurate, automated insights.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ delay: 0.4, duration: 0.6 }} className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button className="bg-white text-gray-900 hover:bg-purple-50 rounded-full px-7 py-6 text-[15px] font-medium shadow-[0_10px_40px_-10px_rgba(255,255,255,0.4)] hover:-translate-y-0.5 transition-all" onClick={() => { window.location.href = '/products'; }}>
              See How It Works <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link href={bookDemoHref('home_bottom')}>
              <Button variant="outline" className="rounded-full border-white/20 text-white bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-md px-7 py-6 text-[15px] font-medium hover:-translate-y-0.5 transition-all">
                Book a Demo
              </Button>
            </Link>
          </motion.div>

        </div>

        <style>{`@keyframes ctaShimmer { 0%{ background-position: 0% 50% } 100%{ background-position: 200% 50% } }`}</style>
      </section>

      {/* ══════════════════════ CONTACT — light section ══════════════════════ */}
      <section className="relative bg-[#FBFAFF] py-20 md:py-28 overflow-hidden">
        <div className="relative container px-6 mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7 }} className="text-center mb-10">
            <div className="text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-medium">Contact</div>
            <h3 className="mt-3 text-3xl md:text-5xl font-semibold tracking-[-0.03em] text-gray-900 leading-[1.05]">Let&apos;s talk.</h3>
            <p className="mt-3 text-gray-500 max-w-md mx-auto">Tell us about your finance stack. We&apos;ll reply within 24h.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ delay: 0.15, duration: 0.7 }}>
            <ContactForm />
          </motion.div>
        </div>
      </section>

      <div style={{ backgroundColor: '#FBFAFF' }}>
        <SecurityBadges />
        <div className="py-6 container px-4 md:px-6 mx-auto max-w-7xl"><LogoMarquee /></div>
      </div>
    </div>
  );
}
